import React from 'react'
import { Box, ButtonBase } from '@mui/material'

import { Icon, IconButton, Typography } from '~components/internal'
import { lightTokens } from '~tokens/internal'

// Where a given issue can be fixed. Used to navigate the editor when an issue is clicked.
export type FlowIssueTarget =
  | { kind: 'block'; blockId: string }
  | { kind: 'formEditor'; blockId: string; stepId: string }
  | { kind: 'billEditor'; stepId: string; blockId?: string }
  | { kind: 'trackerStages'; blockId?: string }
  | { kind: 'addBlocks' }
  | { kind: 'paymentSettings' }

export interface FlowIssue {
  id: string
  description: string
  target?: FlowIssueTarget
}

interface IssuesFlyoutMenuProps {
  issues: FlowIssue[]
  onClose: () => void
  onIssueClick?: (issue: FlowIssue) => void
}

const IssuesFlyoutMenu: React.FC<IssuesFlyoutMenuProps> = ({ issues, onClose, onIssueClick }) => {
  return (
    <Box
      sx={{
        width: 360,
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
        }}
      >
        <Typography variant='body1' sx={{ fontWeight: 700, fontSize: '16px' }}>
          Issues to resolve
        </Typography>
        <IconButton onClick={onClose} size='small' sx={{ padding: '4px' }}>
          <Icon name='Close' size={20} />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          px: 2,
          pb: 2,
          maxHeight: 400,
          overflowY: 'auto',
        }}
      >
        {issues.map((issue) => {
          const navigable = Boolean(issue.target && onIssueClick)
          return (
            <ButtonBase
              key={issue.id}
              component='div'
              disabled={!navigable}
              onClick={navigable ? (): void => onIssueClick!(issue) : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                textAlign: 'left',
                width: '100%',
                gap: 1.5,
                backgroundColor: lightTokens.processFlow.border.error,
                borderRadius: '4px',
                p: 2,
                cursor: navigable ? 'pointer' : 'default',
              }}
            >
              <Icon
                name='Error'
                size={18}
                color={lightTokens.error.main}
                style={{ alignSelf: 'flex-start' }}
              />
              <Typography
                variant='body2'
                sx={{
                  color: lightTokens.error.main,
                  fontSize: '14px',
                  lineHeight: '20px',
                  flex: 1,
                }}
              >
                {issue.description}
              </Typography>
              {navigable && (
                <Icon
                  name='ChevronRight'
                  size={18}
                  color={lightTokens.error.main}
                  style={{ alignSelf: 'center' }}
                />
              )}
            </ButtonBase>
          )
        })}
      </Box>
    </Box>
  )
}

export default IssuesFlyoutMenu
